import React from "react";

import { Breadcrumb, Col, Container, Row } from "react-bootstrap";
import { NavLink } from "react-router-dom";

import { getHomeUrl } from "@/utils/Local";

import i18n from "../i18n";
import { CANONICAL_ORIGIN } from "./Document.component";

export interface IBreadcrumbCrumb {
  label: string;
  // Localized in-app path. Omit for the current page, which renders as plain text.
  to?: string;
}

interface IBreadcrumbProps {
  items: IBreadcrumbCrumb[];
  // Prepend the localized Home crumb. Defaults to true.
  includeHome?: boolean;
  className?: string;
}

// Single source of truth for the trail, so the rendered crumbs and the
// BreadcrumbList structured data can never drift apart.
const resolveCrumbs = (
  items: IBreadcrumbCrumb[],
  includeHome: boolean,
): IBreadcrumbCrumb[] =>
  includeHome ? [{ label: i18n.t("home"), to: getHomeUrl() }, ...items] : items;

// Emits schema.org BreadcrumbList JSON-LD for the same trail. Pass the result
// to DocumentComponent's `jsonLd` prop. The final crumb carries no `item` URL,
// which schema.org allows for the page you are already on.
export const buildBreadcrumbJsonLd = (
  items: IBreadcrumbCrumb[],
  includeHome = true,
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: resolveCrumbs(items, includeHome).map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.label,
    ...(crumb.to ? { item: `${CANONICAL_ORIGIN}${crumb.to}` } : {}),
  })),
});

const BreadcrumbComponent: React.FC<IBreadcrumbProps> = ({
  items,
  includeHome = true,
  className = "",
}) => {
  const { t } = i18n;
  const crumbs = resolveCrumbs(items, includeHome);

  return (
    <Container className="">
      <Row>
        <Col className="mt-3 mb-3" lg={{ span: 10, offset: 1 }} xs={12}>
          <Breadcrumb
            className={`breadcrumbContainer ${className}`.trim()}
            label={t("breadcrumb")}
          >
            {crumbs.map((crumb, index) => {
              const isLast = index === crumbs.length - 1;
              const key = `${crumb.to ?? "current"}-${crumb.label}`;

              // The current page is never a link, regardless of what was passed in.
              if (isLast || !crumb.to) {
                return (
                  <Breadcrumb.Item key={key} active>
                    {crumb.label}
                  </Breadcrumb.Item>
                );
              }

              return (
                <Breadcrumb.Item
                  key={key}
                  linkAs={NavLink}
                  linkProps={{ to: crumb.to }}
                >
                  {crumb.label}
                </Breadcrumb.Item>
              );
            })}
          </Breadcrumb>
        </Col>
      </Row>
    </Container>
  );
};

export default BreadcrumbComponent;
